/**
 * Multi-Lot Aggregation Utilities
 * Functions for aggregating data from multiple parking lots
 */

import { UserLot } from '../types';

// Dashboard Metrics interface
export interface DashboardMetrics {
  date: string;
  totalVehicles: number;
  averageOccupancy: number;
  upTime: number;
  totalRevenue: number;
  pendingRevenue?: number;
  subscriberRevenue?: number;
  currencyUsed?: string;
  totalElectricVehicles: number;
  totalRegisteredVehicles: number;
  totalUnregisteredReturnVehicles: number;
  totalNewVehicles: number;
  totalViolations: number;
  peakHour?: string;
  electricVehicleMakeCount?: Record<string, number>;
  avgDwellRegistered?: number;
  avgDwellNewVehicle?: number;
  avgDwellReturnVisitor?: number;
  avgDwellElectric?: number;
  avgDwellViolator?: number;
  [key: string]: any;
}

// Hourly data interface
export interface HourlyMetrics {
  timestamp: string;
  totalRevenue: number;
  pendingRevenue: number;
  subscriberRevenue: number;
  currencyUsed?: string;
  totalVehicles: number;
  averageOccupancy: number;
  upTime: number;
  totalVehiclesThisHour?: number;
  [key: string]: any;
}

// Session interfaces
export interface ParkingSession {
  sessionId: string;
  plateNumber: string;
  ownerName: string;
  lotId: string;
  entryAlprId: string;
  exitAlprId: string;
  entryTime: string;
  exitTime: string;
  departed: boolean;
  advancedPricingEnabled: boolean;
  forceEnd: boolean;
  pricingId: string;
  totalAmount: number;
  currency?: string;
  vehicleRegistered: boolean;
  sessionType: string;
  userType: string;
  vehicleType: string;
  dwellTime: number;
  dwellPeriodTimeScale: string;
  deleted: boolean;
}

export interface SessionWithLot extends ParkingSession {
  lotName: string;
}

// Violation interfaces
export interface ViolationPlate {
  plateNumber: string;
  openViolations: number;
  totalAmountOwed: number;
  currentlyInLot: boolean;
}

export interface ViolationPlateWithLot extends ViolationPlate {
  lotId: string;
  lotName: string;
}

export interface ViolationDetail {
  violationTrackerId: string;
  plateNumber: string;
  lotId: string;
  vehicleId: string;
  violationType: string;
  violationTimestamp: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleColor: string;
  totalViolationAmount: number | null;
  violationStatus: string;
  evidenceURL: string;
  recordedBy: string;
  recordedOn: string;
  initialNotes: string;
  resolvedBy: string;
  resolvedOn: string;
  resolutionNotes: string;
  dismissedBy: string;
  dismissedOn: string;
  dismissalNotes: string;
  disputedBy: string;
  disputedOn: string;
  disputeNotes: string;
}

export interface ViolationDetailWithLot extends ViolationDetail {
  lotName: string;
}

// Notification interfaces
export interface NotificationItem {
  notificationId: string;
  lotId: string;
  notificationType: string;
  title: string;
  message: string;
  read: boolean;
  notificationTimestamp: string;
  location: string;
  visibleBy: string[];
  createdOn: string;
  createdBy: string;
}

export interface NotificationWithLot extends NotificationItem {
  lotName: string;
}

// Registry interfaces
export interface RegistryEntry {
  registryId?: string;
  plateNumber: string;
  lotId: string;
  name?: string;
  email?: string;
  phone?: string;
  phoneNumber?: string;
  createdOn?: string;
  modifiedOn?: string;
}

export interface RegistryEntryWithLot extends RegistryEntry {
  lotName: string;
}

// Ignored ALPR strings interface
export interface IgnoredAlprString {
  ignoredStringId?: string;
  lotId: string;
  ignoredString: string;
  createdOn?: string;
}

/**
 * Aggregates dashboard analytics data from multiple lots
 * Sums totals and averages rates/percentages
 */
export const aggregateAnalytics = (
  dataArrays: DashboardMetrics[][],
  lotIds: string[]
): DashboardMetrics[] => {
  if (!dataArrays.length || dataArrays.every(arr => !arr || arr.length === 0)) {
    return [];
  }

  // Group data by date across all lots
  const dateMap = new Map<string, DashboardMetrics[]>();

  dataArrays.forEach((lotData) => {
    if (!lotData) return;
    lotData.forEach((dayData) => {
      const existing = dateMap.get(dayData.date) || [];
      existing.push(dayData);
      dateMap.set(dayData.date, existing);
    });
  });

  // Aggregate each date
  const aggregated: DashboardMetrics[] = [];

  dateMap.forEach((entries, date) => {
    const numLots = entries.length;

    // Sum fields
    const sumFields = [
      'totalVehicles',
      'totalRevenue',
      'pendingRevenue',
      'subscriberRevenue',
      'totalElectricVehicles',
      'totalRegisteredVehicles',
      'totalUnregisteredReturnVehicles',
      'totalNewVehicles',
      'totalViolations',
    ];

    // Average fields
    const avgFields = [
      'averageOccupancy',
      'upTime',
      'avgDwellRegistered',
      'avgDwellNewVehicle',
      'avgDwellReturnVisitor',
      'avgDwellElectric',
      'avgDwellViolator',
    ];

    const result: DashboardMetrics = {
      date,
      totalVehicles: 0,
      averageOccupancy: 0,
      upTime: 0,
      totalRevenue: 0,
      currencyUsed: undefined,
      totalElectricVehicles: 0,
      totalRegisteredVehicles: 0,
      totalUnregisteredReturnVehicles: 0,
      totalNewVehicles: 0,
      totalViolations: 0,
    };

    // Sum the sum fields
    sumFields.forEach((field) => {
      result[field] = entries.reduce((sum, entry) => {
        const value = entry[field];
        return sum + (typeof value === 'number' ? value : 0);
      }, 0);
    });

    // Average the avg fields (only count non-zero values)
    avgFields.forEach((field) => {
      const values = entries.map(e => e[field]).filter(v => typeof v === 'number' && v > 0);
      if (values.length > 0) {
        result[field] = values.reduce((sum, v) => sum + (v as number), 0) / values.length;
      } else {
        result[field] = 0;
      }
    });

    const currencyValues = new Set(
      entries
        .map(e => e.currencyUsed)
        .filter((value): value is string => Boolean(value))
    );
    if (currencyValues.has('MIXED') || currencyValues.size > 1) {
      result.currencyUsed = 'MIXED';
    } else if (currencyValues.size === 1) {
      result.currencyUsed = Array.from(currencyValues)[0];
    }

    // Merge EV make counts
    const evMakeCounts: Record<string, number> = {};
    entries.forEach((entry) => {
      if (entry.electricVehicleMakeCount) {
        Object.entries(entry.electricVehicleMakeCount).forEach(([make, count]) => {
          evMakeCounts[make] = (evMakeCounts[make] || 0) + count;
        });
      }
    });
    if (Object.keys(evMakeCounts).length > 0) {
      result.electricVehicleMakeCount = evMakeCounts;
    }

    aggregated.push(result);
  });

  // Sort by date
  return aggregated.sort((a, b) => a.date.localeCompare(b.date));
};

/**
 * Aggregates hourly data from multiple lots
 */
export const aggregateHourlyData = (
  dataArrays: { success: boolean; data: HourlyMetrics[] }[]
): { success: boolean; data: HourlyMetrics[] } => {
  const allData = dataArrays.flatMap(d => d?.data || []);

  if (allData.length === 0) {
    return { success: true, data: [] };
  }

  // Group by timestamp/hour
  const hourMap = new Map<string, HourlyMetrics[]>();

  allData.forEach((hourData) => {
    const hour = hourData.timestamp;
    const existing = hourMap.get(hour) || [];
    existing.push(hourData);
    hourMap.set(hour, existing);
  });

  const aggregated: HourlyMetrics[] = [];

  hourMap.forEach((entries, timestamp) => {
    const result: HourlyMetrics = {
      timestamp,
      totalRevenue: entries.reduce((sum, e) => sum + (e.totalRevenue || 0), 0),
      pendingRevenue: entries.reduce((sum, e) => sum + (e.pendingRevenue || 0), 0),
      subscriberRevenue: entries.reduce((sum, e) => sum + (e.subscriberRevenue || 0), 0),
      currencyUsed: undefined,
      totalVehicles: entries.reduce((sum, e) => sum + (e.totalVehicles || 0), 0),
      totalVehiclesThisHour: entries.reduce((sum, e) => sum + (e.totalVehiclesThisHour || 0), 0),
      averageOccupancy: entries.reduce((sum, e) => sum + (e.averageOccupancy || 0), 0) / entries.length,
      upTime: entries.reduce((sum, e) => sum + (e.upTime || 0), 0) / entries.length,
    };

    const currencyValues = new Set(
      entries
        .map(e => e.currencyUsed)
        .filter((value): value is string => Boolean(value))
    );
    if (currencyValues.has('MIXED') || currencyValues.size > 1) {
      result.currencyUsed = 'MIXED';
    } else if (currencyValues.size === 1) {
      result.currencyUsed = Array.from(currencyValues)[0];
    }

    aggregated.push(result);
  });

  return {
    success: true,
    data: aggregated.sort((a, b) => a.timestamp.localeCompare(b.timestamp)),
  };
};

/**
 * Aggregates sessions from multiple lots, adding lot name to each
 */
export const aggregateSessions = (
  sessionsArrays: ParkingSession[][],
  lotNames: Map<string, string>
): SessionWithLot[] => {
  const allSessions: SessionWithLot[] = [];

  sessionsArrays.forEach((sessions) => {
    if (!sessions) return;
    sessions.forEach((session) => {
      allSessions.push({
        ...session,
        lotName: lotNames.get(session.lotId) || 'Unknown Lot',
      });
    });
  });

  // Sort by entry time (most recent first)
  return allSessions.sort((a, b) =>
    new Date(b.entryTime).getTime() - new Date(a.entryTime).getTime()
  );
};

/**
 * Aggregates violations from multiple lots
 */
export const aggregateViolations = (
  violationsArrays: ViolationPlate[][],
  lotIds: string[],
  lotNames: Map<string, string>
): ViolationPlateWithLot[] => {
  const allViolations: ViolationPlateWithLot[] = [];

  violationsArrays.forEach((violations, index) => {
    if (!violations) return;
    const lotId = lotIds[index];
    violations.forEach((violation) => {
      allViolations.push({
        ...violation,
        lotId,
        lotName: lotNames.get(lotId) || 'Unknown Lot',
      });
    });
  });

  // Sort by total amount owed (highest first)
  return allViolations.sort((a, b) => b.totalAmountOwed - a.totalAmountOwed);
};

/**
 * Aggregates violation details from multiple lots
 */
export const aggregateViolationDetails = (
  detailsArrays: ViolationDetail[][],
  lotNames: Map<string, string>
): ViolationDetailWithLot[] => {
  const allDetails: ViolationDetailWithLot[] = [];

  detailsArrays.forEach((details) => {
    if (!details) return;
    details.forEach((detail) => {
      allDetails.push({
        ...detail,
        lotName: lotNames.get(detail.lotId) || 'Unknown Lot',
      });
    });
  });

  return allDetails.sort((a, b) =>
    new Date(b.violationTimestamp).getTime() - new Date(a.violationTimestamp).getTime()
  );
};

/**
 * Aggregates notifications from multiple lots
 */
export const aggregateNotifications = (
  notificationsArrays: NotificationItem[][],
  lotNames: Map<string, string>
): NotificationWithLot[] => {
  const allNotifications: NotificationWithLot[] = [];

  notificationsArrays.forEach((notifications) => {
    if (!notifications) return;
    notifications.forEach((notification) => {
      allNotifications.push({
        ...notification,
        lotName: lotNames.get(notification.lotId) || 'Unknown Lot',
      });
    });
  });

  // Sort by timestamp (most recent first)
  return allNotifications.sort((a, b) =>
    new Date(b.notificationTimestamp).getTime() - new Date(a.notificationTimestamp).getTime()
  );
};

/**
 * Aggregates plate registry entries from multiple lots
 * Merges entries with the same plate number
 */
export const aggregateRegistry = (
  registryArrays: RegistryEntry[][]
): RegistryEntry[] => {
  const plateMap = new Map<string, RegistryEntry>();

  registryArrays.forEach((entries) => {
    if (!entries) return;
    entries.forEach((entry) => {
      // Keep first occurrence of each plate
      if (!plateMap.has(entry.plateNumber)) {
        plateMap.set(entry.plateNumber, entry);
      }
    });
  });

  return Array.from(plateMap.values()).sort((a, b) =>
    a.plateNumber.localeCompare(b.plateNumber)
  );
};

/**
 * Aggregates ignored ALPR strings from multiple lots
 * Returns unique strings across all lots
 */
export const aggregateIgnoredStrings = (
  ignoredArrays: IgnoredAlprString[][]
): string[] => {
  const uniqueStrings = new Set<string>();

  ignoredArrays.forEach((strings) => {
    if (!strings) return;
    strings.forEach((item) => {
      uniqueStrings.add(item.ignoredString);
    });
  });

  return Array.from(uniqueStrings).sort();
};

/**
 * Sums capacities across multiple lots
 */
export const sumCapacities = (lots: UserLot[]): number => {
  return lots.reduce((sum, lot) => sum + (lot.lotCapacity || 0), 0);
};

/**
 * Sums current occupancies from multiple lot data
 */
export const sumOccupancies = (occupancies: (number | null)[]): number => {
  return occupancies.reduce<number>((sum, occ) => sum + (occ ?? 0), 0);
};

/**
 * Helper to create lot name map from UserLot array
 */
export const createLotNameMap = (lots: UserLot[]): Map<string, string> => {
  const map = new Map<string, string>();
  lots.forEach(lot => {
    map.set(lot.lotId, lot.lotName);
  });
  return map;
};
